/* eslint-disable @typescript-eslint/member-ordering */
import * as angular from 'angular';
import * as _ from 'lodash';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { AppStore } from '~/types/main.types';

import { Color } from '@mode/capra';
import { ReportParameterOptionsFacade } from '@mode/reports/domain';
import {
  ErrorReporter,
  FeatureFlag,
  FeatureFlagsFacade,
  ParameterOptions,
  ReportTypes,
} from '@mode/shared/contract-common';
import { FrequencyType, Emailable, ScheduleResource } from '@mode/shared/schedules/contract';
import { ConfirmDialogService } from '@mode/shared/ui';
import { ReportRunFormField } from 'libs/shared/contract-common/src/lib/report';
import { ReportDetailsScheduleFacade } from './report-details-schedule.facade';
import {
  ScheduleModalFacade,
  ScheduleOwnerFacade,
  ScheduleSlackFacade,
  ScheduleSubscriptionFacade,
} from '@mode/shared/schedules/contract';
import { SchedulableEntity } from '@mode/shared/schedules/ui';
import { DatasetFacade } from '@mode/dataset/contract';

const ALL_SELECTED_OPTION = 'all';

export default class NewSchedulesController implements angular.IController {
  initializingSchedules: boolean;
  loadingReportParams: boolean;
  optionsLoadTriggered: boolean;
  showScheduleForm: boolean;
  schedules: any[];
  scheduleTitle$: Observable<string>;
  views: {
    index: boolean;
    show: boolean;
    edit: boolean;
    users: boolean;
    loading: boolean;
    scheduleTime: string;
  };
  editForm: any;
  schedule: any;
  isScheduleNameEditMode: boolean;
  isScheduleNameValid: boolean;
  showNamedSchedules: boolean | unknown;
  adminManagedScheduleIntervals: boolean | unknown;
  subscription: any;
  subscriptionEditForm: any;
  report: any;
  run: any;
  account: any;
  owner: any;
  startSchedule: any;
  notify: {
    type: string;
    options: any[];
  };
  reportSchedulesCollection: any;
  subscriptionMembershipsCollection: any;
  userMembershipsCollection: any;
  slackMembershipsCollection: any;
  userMemberships: any[];
  slackMemberships: any[];
  scheduleEditForm: any;
  view: string;
  selectedChannel: any;
  dismiss: any;
  slackScheduleTooltipOpened: boolean;
  reportParameterOptionsLoading$: Observable<boolean>;
  reportParameterOptionsComplete$: Observable<boolean>;
  paramsSubscription: Subscription;
  loadingScheduleFormSubscription: Subscription;
  defaultOptions: Record<string, ParameterOptions[]> = {};
  parameterOptionCount: Record<string, number> = {};
  onUpdate: () => void;
  workspaceTitle = 'Workspace';
  externalSharing: boolean;
  disableExternalSharing: boolean;
  entity$: Observable<SchedulableEntity>;
  isNewSchedule = false;
  dayOfTheWeek = '';

  public emailSubscribers = [];

  public readonly neutralButtonColor: Color = 'neutral';
  public hasStartTime: boolean;
  public slackDeliveryOptionEnabled = false;

  static $inject = [
    '$scope',
    'ReportSubscriptionModel',
    'ReportScheduleModel',
    'EmailSubscriptionMembershipModel',
    'SlackSubscriptionMembershipModel',
    '$q',
    'ToastService',
    'ModeCollection',
    'ReportRunEmailModel',
    'ReportParameterOptionsFacade',
    '$state',
    'ConfirmDialogService',
    'ErrorReporter',
    'ScheduleModalFacade',
    'ScheduleSubscriptionFacade',
    'ReportDetailsScheduleFacade',
    'ScheduleSlackFacade',
    'ScheduleOwnerFacade',
    'DatasetFacade',
    'FeatureFlagsFacade',
  ];

  constructor(
    private $scope,
    private ReportSubscriptionModel,
    private ReportScheduleModel,
    private EmailSubscriptionMembershipModel,
    private SlackSubscriptionMembershipModel,
    private $q,
    private toastService,
    private ModeCollection,
    private ReportRunEmailModel,
    private reportParameterOptionsFacade: ReportParameterOptionsFacade,
    private $state,
    private confirmDialogService: ConfirmDialogService,
    private errorReporter: ErrorReporter,
    public scheduleModalFacade: ScheduleModalFacade,
    public scheduleSubscriptionFacade: ScheduleSubscriptionFacade,
    public reportDetailsScheduleFacade: ReportDetailsScheduleFacade,
    public scheduleSlackFacade: ScheduleSlackFacade,
    public scheduleOwnerFacade: ScheduleOwnerFacade,
    public datasetFacade: DatasetFacade,
    public featureFlagsFacade: FeatureFlagsFacade
  ) {
    this.initializingSchedules = true;
    this.optionsLoadTriggered = false;
    this.slackScheduleTooltipOpened = false;
    this.showScheduleForm = true;
    this.schedules = [];
    this.views = {
      index: true,
      show: false,
      edit: false,
      users: false,
      scheduleTime: 'hour',
      loading: false,
    };
    this.notify = {
      type: 'none',
      options: [
        ['None (data refresh only)', 'none'],
        ['Email', 'email'],
      ],
    };

    this.reportParameterOptionsLoading$ = this.reportParameterOptionsFacade.reportParameterOptionsLoading$;
    this.reportParameterOptionsComplete$ = this.reportParameterOptionsFacade.reportParameterOptionsComplete$;
  }

  $onInit() {
    this.isScheduleNameValid = false;
    this.featureFlagsFacade.asObservable(FeatureFlag.ShowNamedSchedules).subscribe((flag) => {
      this.showNamedSchedules = flag;
    });

    this.featureFlagsFacade.asObservable(FeatureFlag.AdminManagedScheduleIntervals).subscribe((flag) => {
      this.adminManagedScheduleIntervals = flag;
    });

    this.scheduleOwnerFacade.loadOwner(this.owner);
    this.scheduleSlackFacade.initialize(this.owner, this.ModeCollection);
    this.fetchReportParameterOptions();

    this.entity$ = this.datasetFacade.isSheetDataset$.pipe(
      map((isSheetDataset) => {
        if (isSheetDataset) {
          return 'gsheetDataset';
        } else if (this.report.type === ReportTypes.ReportType.DatasetReport) {
          return 'sqlDataset';
        } else {
          return 'report';
        }
      })
    );
    this.scheduleTitle$ = this.entity$.pipe(map((entity) => (entity === 'report' ? 'Report' : 'Dataset')));

    this.loadSchedules();

    //initialize to edit if schedule is passed in
    if (this.startSchedule) {
      const self = this;
      this.startSchedule
        .reportSubscriptions()
        .then(this.ModeCollection.elements)
        .then(function (subscriptions) {
          self.setDisableExternalSharing();

          if (subscriptions.length) {
            self.editSchedule(self.startSchedule, subscriptions[0]);
            if (self.view && self.view === 'users' && self.account.email_verified) {
              self.setView('loading');
              self.initUsers.call(self);
            }
          } else {
            self.editSchedule(self.startSchedule, undefined);
          }
        })
        .catch((err) => this.errorReporter.notify({ error: err }));
    } else if (this.view && this.view === 'new') {
      this.newSchedule();
    }

    if (this.owner && this.ModeCollection) {
      this.scheduleSlackFacade
        .tryToInitialize()
        .then((slackInitialized) => {
          if (slackInitialized) {
            this.addSlackToDeliveryOptions();
            this.slackDeliveryOptionEnabled = true;
          }
        })
        .catch((err) => this.errorReporter.notify({ error: err }));
    }

    this.$scope.$watch('ctrl.schedule.name', (newVal) => {
      if (newVal && (newVal.length < 4 || newVal.length > 64)) {
        this.isScheduleNameValid = false;
      } else {
        this.isScheduleNameValid = true;
      }
    });
  }

  private addSlackToDeliveryOptions() {
    this.notify.options.splice(this.notify.options.length - 1, 0, ['Slack', 'slack']);
  }

  loadSchedules() {
    return this.$q
      .all([
        this.report.reportSchedules({
          includes: {
            report_schedules: {
              report_subscriptions: {
                report_subscriptions: [
                  'new_email_subscription_membership',
                  'slack_subscription_memberships',
                  'new_slack_subscription_membership',
                ],
              },
            },
          },
          refresh: true,
        }),
        this.report.perspectiveEmailSubscriptionMemberships({ refresh: true }),
      ])
      .then(
        this.$q.spread((reportSchedulesCollection, userMembershipsCollection) => {
          this.userMembershipsCollection = userMembershipsCollection;
          const self = this;
          userMembershipsCollection.watch(this.$scope, function (userMemberships) {
            self.userMemberships = userMemberships;
            if (!self.initializingSchedules) {
              self.includeUserMemberships.call(self, self.schedules);
            }
          });

          this.reportSchedulesCollection = reportSchedulesCollection;
          reportSchedulesCollection.watch(this.$scope, function (reportSchedules) {
            self.includeSubscriptions
              .call(self, reportSchedules)
              .then(self.includeUserMemberships.bind(self))
              .then((schedulesWithSubscriptionsAndUserMemberships) => {
                self.initializingSchedules = false;
                self.schedules = schedulesWithSubscriptionsAndUserMemberships;
                self.scheduleModalFacade.loadRawSchedules(schedulesWithSubscriptionsAndUserMemberships);
                self.reportDetailsScheduleFacade.loadRawSchedulesViewDetails(
                  schedulesWithSubscriptionsAndUserMemberships
                );
              })
              .catch((err) => this.errorReporter.notify({ error: err }));
          });
        })
      )
      .catch((err) => this.errorReporter.notify({ error: err }));
  }

  setDisableExternalSharing() {
    this.disableExternalSharing =
      !this.subscriptionEditForm?.data_previews_enabled &&
      !this.subscriptionEditForm?.data_tables_enabled &&
      !this.subscription?.csv_attachments_enabled;
  }

  isSlackSchedule(subscription) {
    return !subscription
      ? false
      : !!(subscription && subscription.savedSlackMemberships && subscription.savedSlackMemberships.length);
  }

  includeSubscriptions(schedules) {
    return this.$q.all(
      schedules.map((schedule) =>
        schedule.savedSubscriptions
          ? schedule
          : schedule
              .reportSubscriptions({ useEntryPath: true })
              .then(this.ModeCollection.elements)
              .then((subscriptions) =>
                this.ReportSubscriptionModel.buildSubscriptionWithSlackMembership(subscriptions).then(
                  (subscriptionsWithSlackMemberships) => {
                    schedule.savedSubscriptions = subscriptionsWithSlackMemberships;
                    return schedule;
                  }
                )
              )
      )
    );
  }

  isMembershipEqual(membership, subscription) {
    return subscription.id === membership._resource.links('report_subscription').href;
  }

  includeUserMemberships(schedulesWithSubscriptions) {
    return schedulesWithSubscriptions.map((schedule) => {
      if (schedule.savedSubscriptions && schedule.savedSubscriptions.length && this.userMemberships) {
        const userMembership = _.find(this.userMemberships, (membership) =>
          this.isMembershipEqual(membership, schedule.savedSubscriptions[0])
        );
        if (userMembership) {
          schedule.userMembership = userMembership;
        } else {
          schedule.userMembership = null;
        }
      } else {
        schedule.userMembership = null;
      }
      return schedule;
    });
  }

  setView(view) {
    //setView gets called whenever there is a save or update action on schedule modal, so we need to call onUpdate() to update org settings schedules list
    this.onUpdate();
    this.views.index = false;
    this.views.show = false;
    this.views.edit = false;
    this.views.users = false;
    this.views.loading = false;

    this.views[view] = true;
  }

  fetchReportParameterOptions() {
    // For legacy flow, or when there are no dynamic multiselect form fields, we do not need to fetch options separately

    const fields: ReportTypes.ReportRunFormField[] = this.run.form_fields;
    const areDynamic: boolean = fields?.some((f) => f.is_dynamic);
    if (!areDynamic) {
      return;
    }

    const isViewReport = this.$state.includes('reportShow');
    if (!this.optionsLoadTriggered && !isViewReport) {
      this.optionsLoadTriggered = true;
      this.loadingScheduleFormSubscription = combineLatest([
        this.reportParameterOptionsLoading$,
        this.reportParameterOptionsComplete$,
      ]).subscribe(([loading, complete]) => {
        this.showScheduleForm = !loading && complete;
      });
      this.reportParameterOptionsFacade.loadReportParameterOptions(
        this.report._resource.account_username,
        this.report.token,
        this.run.token
      );
    }

    this.paramsSubscription = combineLatest([
      this.reportParameterOptionsFacade.reportParameterOptions$,
      this.reportParameterOptionsFacade.reportParameterOptionsComplete$,
    ])
      .pipe(filter(([, complete]) => complete))
      .subscribe(([reportParameterOptions]) => {
        reportParameterOptions.forEach((parameterOptions) => {
          const fieldIndex = fields.findIndex((f) => f.name === parameterOptions.name);
          if (fieldIndex !== undefined && fieldIndex >= 0) {
            fields[fieldIndex] = {
              ...fields[fieldIndex],
              options: parameterOptions.options,
              source_size: parameterOptions.sourceSize,
              options_error: parameterOptions.optionsError,
            };
          }
        });
        const editSchedule = !!this.schedule;

        if (this.account.email_verified && editSchedule) {
          this.mergeFormFieldParams(editSchedule);
        }
      });
  }

  newSchedule() {
    this.isNewSchedule = true;
    if (this.account.email_verified) {
      this.schedule = this.report.modelFromForm_reportSchedules();
      this.editForm = this.schedule._formElements;
      this.scheduleModalFacade.setFrequencyType(this.schedule.cron.freq);
      this.mergeFormFieldParams();
      this.setHasStartTime();

      // randomize hour that daily schedule runs
      const randomHour = Math.floor(Math.random() * 23);
      this.schedule.cron.hour = randomHour;

      this.schedule.slackChannel = this.selectedChannel;
      this.schedule.name = '';
      this.subscription = this.report.modelFromForm_reportSubscriptions();
      this.subscriptionEditForm = this.subscription._formElements;

      this.setDisableExternalSharing();
      this.scheduleModalFacade.setActiveSchedule(this.schedule);
      this.setView('edit');
    }
  }

  editReportSchedule(schedule) {
    this.loadSchedules().then(() => {
      const convertedSchedule = this.scheduleModalFacade.schedules.find(
        (legacySchedule) => legacySchedule.token === schedule.id
      );
      this.editSchedule(convertedSchedule, convertedSchedule.savedSubscriptions[0] ?? null);
    });
  }

  editSchedule(schedule, subscription) {
    this.isNewSchedule = false;
    if (this.account.email_verified) {
      this.schedule = schedule;
      this.setHasStartTime();
      this.schedule.slackChannel = this.selectedChannel;
      this.editForm = this.schedule._resource._forms.edit._inputs.report_schedule;
      this.dayOfTheWeek = schedule.cron.day_of_week;
      // schedule.parameters are existing values from backend.
      // schedule.params are existing values with default values prefilled, if blank/undefined for a key
      // remove prefilled values if blank so input fields also show blank
      Object.keys(this.editForm.params).forEach((key) => {
        if (!this.schedule.parameters[key]) {
          delete this.schedule.params[key];
        }
      });
      this.mergeFormFieldParams(true);

      this.notify.type = subscription
        ? subscription.savedSlackMemberships && subscription.savedSlackMemberships.length
          ? 'slack'
          : 'email'
        : 'none';
      this.subscription = subscription ? subscription : this.report.modelFromForm_reportSubscriptions();
      this.subscriptionEditForm = this.subscription._new_reportSubscription
        ? this.subscription._formElements
        : this.subscription._resource._forms.edit._inputs.report_subscription;

      if (this.isSlackSchedule(this.subscription)) {
        this.setView('loading');
        try {
          this.schedule.slackChannel = this.scheduleSlackFacade.getSlackChannel(
            this.subscription.savedSlackMemberships[0]
          );
        } finally {
          this.setView('edit');
        }
      } else {
        this.setView('edit');
      }
      this.scheduleModalFacade.setActiveSchedule(this.schedule);
      this.scheduleModalFacade.setFrequencyType(this.scheduleModalFacade.activeSchedule.cleanSchedule.frequency);
      this.setDisableExternalSharing();
    }
  }

  removeNonExistantValuesFromParams(fields: any, editForm: any, schedule: ScheduleResource) {
    for (const key in fields) {
      if (fields.hasOwnProperty(key) && fields[key].type === 'multiselect') {
        const options = fields[key].options;
        const value = fields[key].value;

        // Filter the values that are not present in the options array
        fields[key].value = value.filter((val) => options.some((option) => option.includes(val)));
        if (editForm.params[key] && editForm.params[key].value[0] !== 'all') {
          editForm.params[key].value = editForm.params[key].value.filter((val) =>
            options.some((option) => option.includes(val))
          );
        }
        if (schedule.params[key] && schedule.params[key][0] !== 'all') {
          schedule.params[key] = schedule.params[key].filter((val) => options.some((option) => option.includes(val)));
        }
      }
    }
  }

  mergeFormFieldParams(noOverride = false) {
    if (this.run) {
      const fields: any = _.keyBy(this.run.form_fields, 'name');
      this.removeNonExistantValuesFromParams(fields, this.editForm, this.schedule);
      const paramValues = {};
      if (noOverride) {
        // Keep a copy of values to use later, since they get overwritten by _.merge call below
        Object.keys(this.editForm.params).forEach((key) => {
          const value = this.editForm.params[key].value;
          paramValues[key] = _.isArray(value) ? [...value] : value;
        });
      }
      // update existing schedule edit form with params
      _.merge(this.editForm.params, fields);
      _.keys(this.editForm.params).forEach((key) => {
        const param: ReportRunFormField = this.editForm.params[key];

        if (param.type === 'multiselect') {
          if (noOverride) {
            param.value = paramValues[key];
          }
          const isMultiselectAllByDefault = param.value?.length > 0 && param.value[0] === ALL_SELECTED_OPTION;

          this.defaultOptions[key] = param.options
            .map(([label, value]) => ({
              isSelected: isMultiselectAllByDefault ? true : param.value?.some((x) => x == value),
              label,
              value,
            }))
            .filter((option) => {
              return option.label !== 'All';
            });

          this.parameterOptionCount[key] = param.options.length;
        }
      });

      if (!noOverride) {
        // override new schedule with past generated run params
        _.keys(fields).forEach((key) => {
          this.schedule.params[key] = fields[key].value;
        });
      }
    }
  }

  editOrNew(schedule) {
    this.setView('edit');
  }

  scheduleParamsEmpty() {
    if (!this.schedule || !this.editForm) {
      return true;
    }
    return angular.equals({}, this.editForm.params);
  }

  deleteSchedule(schedule) {
    schedule
      .destroy()
      .then(() => {
        this.onUpdate(); //Update org settings schedules list
        if (!this.views.index) {
          this.dismiss();
        }
        this.toastService.success('Deleted schedule');
      })
      .catch((e) => {
        this.errorReporter.notify({ error: e });
        this.toastService.error('Could not delete schedule');
      });
  }

  saveOrNext() {
    const self = this;
    this.saveSchedule().then(function () {
      self.isScheduleNameEditMode = true;
      if (self.notify.type === 'none' || self.notify.type === 'slack') {
        self.setView('index');
      } else {
        self.initUsers.call(self);
      }
    });
  }

  initUsers() {
    const self = this;
    this.subscription.emailSubscriptionMemberships({ refresh: true }).then((subscriptionMembershipsCollection) => {
      self.subscriptionMembershipsCollection = subscriptionMembershipsCollection;
      subscriptionMembershipsCollection.watch(self.$scope, function (subscriptionMemberships) {
        self.emailSubscribers = subscriptionMemberships;
        // update users list with new or removed memberships
      });
      subscriptionMembershipsCollection.elements().then((subscriptionMemberships) => {
        self.setView('users');
      });
    });
  }

  public removeSubscription(membership) {
    membership
      .destroy()
      .then(() => {
        this.toastService.success('Removed email');
        this.subscription.email_subscriber_count--;
      })
      .catch((e) => {
        this.errorReporter.notify({ error: e });
        this.toastService.error('Could not remove email');
      });
  }

  public addSubscription(membership: Emailable) {
    this.EmailSubscriptionMembershipModel.create({
      reportSubscription: this.subscription.id,
      params: {
        'membership.subscriber_email': membership.email,
      },
    })
      .then((newMembership) => {
        this.toastService.success('Added email');
        this.subscription.email_subscriber_count++;
        this.subscriptionMembershipsCollection.insert(newMembership);
      })
      .catch((e) => {
        this.errorReporter.notify({ error: e });
        this.toastService.error('Error adding email');
      });
  }

  saveSchedule() {
    if (this.schedule._new_reportSchedule) {
      switch (this.notify.type) {
        case 'none':
          return this.createSchedule.call(this);
        case 'email':
          return this.createScheduleAndSubscription.call(this);
        case 'slack':
          return this.createScheduleAndSubscription.call(this).then((subscription) =>
            this.SlackSubscriptionMembershipModel.create({
              reportSubscription: subscription.id,
              params: {
                'membership.subscriber_channel': this.schedule.slackChannel.name,
                'membership.subscriber_channel_id': this.schedule.slackChannel.id,
              },
            })
          );
      }
    } else {
      switch (this.notify.type) {
        case 'none':
          return this.updateSchedule.call(this);
        case 'email':
          return this.updateScheduleAndSubscription.call(this);
        case 'slack':
          return this.updateScheduleAndSubscription.call(this).then(() =>
            this.SlackSubscriptionMembershipModel.create({
              reportSubscription: this.subscription.id,
              params: {
                'membership.subscriber_channel': this.schedule.slackChannel.name,
                'membership.subscriber_channel_id': this.schedule.slackChannel.id,
              },
            }).then((membership) => this.subscription.insertSlackMembership(membership))
          );
      }
    }
  }

  updateSchedule() {
    const deferred = this.$q.defer();
    if (!this.subscription._new_reportSubscription) {
      this.subscription.destroy().then(() => {
        this.schedule.savedSubscriptions = null;
      });
    }
    const shouldUpdate = this.scheduleEditForm.$dirty || this.scheduleModalFacade.hasUpdate;
    if (shouldUpdate) {
      return this.ReportScheduleModel.update(this.schedule)
        .then((schedule) => {
          this.reportDetailsScheduleFacade.cleanUpFrequencyText(schedule);
          this.toastService.success('Updated schedule');
          deferred.resolve();
        })
        .catch((e) => {
          this.errorReporter.notify({ error: e });
          this.toastService.error('Schedule did not update properly');
          deferred.reject(e);
        });
    } else {
      deferred.resolve();
    }
    return deferred.promise;
  }

  updateScheduleAndSubscription() {
    const deferred = this.$q.defer();
    const shouldUpdate = this.scheduleEditForm.$dirty || this.scheduleModalFacade.hasUpdate;

    function cleanUpMemberships() {
      this.subscription.destroySavedSlackMemberships().then(() => {
        if (this.notify.type === 'slack') {
          this.subscription
            .emailSubscriptionMemberships({ refresh: true })
            .then(this.ModeCollection.elements)
            .then((emailMemberships) => this.$q.all(emailMemberships.map((membership) => membership.destroy())))
            .then(() => {
              deferred.resolve();
              this.AlertService.success('Updated schedule');
            });
        } else {
          deferred.resolve();
          this.AlertService.success('Updated schedule');
        }
      });
    }

    if (this.subscription._new_reportSubscription) {
      const self = this;
      this.ReportScheduleModel.update(this.schedule)
        .then(function (schedule) {
          const params = self.subscription.toParams();
          params['report_subscription.schedule_token'] = self.schedule.token;
          params.includes = ['report_schedule', 'new_email_subscription_membership'];
          self.ReportSubscriptionModel.create({
            report: self.report.token,
            params: params,
          })
            .then((subscription) => {
              self.reportDetailsScheduleFacade.cleanUpFrequencyText(schedule);
              self.subscription = subscription;
              self.schedule.savedSubscriptions = [subscription];
              self.toastService.success('Updated schedule');
              deferred.resolve();
            })
            .catch((e) => {
              self.errorReporter.notify({ error: e });
              self.toastService.error('Schedule did not update properly');
              deferred.reject(e);
            });
        })
        .catch((e) => {
          self.errorReporter.notify({ error: e });
          self.toastService.error('Schedule did not update properly');
          deferred.reject(e);
        });
    } else if (shouldUpdate) {
      this.$q
        .all([this.ReportSubscriptionModel.update(this.subscription), this.ReportScheduleModel.update(this.schedule)])
        .then(([_subscription, schedule]) => {
          this.reportDetailsScheduleFacade.cleanUpFrequencyText(schedule);
        })
        .then(cleanUpMemberships.bind(this))
        .catch((e) => {
          this.errorReporter.notify({ error: e });
          this.toastService.error('Schedule did not update properly');
          deferred.reject(e);
        });
    } else {
      deferred.resolve();
    }
    return deferred.promise;
  }

  createSchedule() {
    const deferred = this.$q.defer();
    this.ReportScheduleModel.create({
      report: this.report.token,
      params: this.schedule.toParams(),
    })
      .then((schedule) => {
        this.schedule = schedule;
        this.scheduleModalFacade.setActiveSchedule(this.schedule);
        this.toastService.success('Created schedule');
        this.reportSchedulesCollection.insert(schedule);
        deferred.resolve();
      })
      .catch((e) => {
        this.errorReporter.notify({ error: e });
        this.toastService.error('Could not create schedule');
        deferred.reject(e);
      });
    return deferred.promise;
  }

  createScheduleAndSubscription() {
    const deferred = this.$q.defer();
    const params = this.subscription.toParams();
    params.includes = ['report_schedule', 'new_email_subscription_membership', 'new_slack_subscription_membership'];
    const scheduleParams = this.schedule.toParams();
    const self = this;
    Object.keys(scheduleParams).forEach((key) => {
      params['report_subscription.' + key] = scheduleParams[key];
    });

    this.ReportSubscriptionModel.create({
      report: this.report.token,
      params: params,
    })
      .then(function (subscription) {
        self.subscription = subscription;
        self.toastService.success('Created schedule');
        subscription.reportSchedule().then(function (schedule) {
          self.reportSchedulesCollection.insert(schedule);
        });
        deferred.resolve(subscription);
      })
      .catch((e) => {
        self.errorReporter.notify({ error: e });
        self.toastService.error('Could not create schedule');
        deferred.reject(e);
      });
    return deferred.promise;
  }

  testEmail(subscription) {
    const params = {
      'report_run_email.recipient_address': this.account.email,
      'report_run_email.message': '',
    };

    const formElements = subscription._new_reportSubscription
      ? subscription._formElements
      : subscription._resource._forms.edit._inputs.report_subscription;
    if (formElements.csv_attachments_enabled) {
      params['report_run_email.csv_attachments_enabled'] = subscription.csv_attachments_enabled;
    }
    if (formElements.pdf_attachments_enabled) {
      params['report_run_email.pdf_attachments_enabled'] = subscription.pdf_attachments_enabled;
    }
    if (formElements.data_previews_enabled) {
      params['report_run_email.data_previews_enabled'] = subscription.data_previews_enabled;
    }
    if (formElements.data_tables_enabled) {
      params['report_run_email.data_tables_enabled'] = subscription.data_tables_enabled;
    }

    // Force 'report_links_enabled' when disableExternalSharing is true
    if (formElements.report_links_enabled || this.disableExternalSharing) {
      params['report_run_email.report_links_enabled'] =
        this.disableExternalSharing || subscription.report_links_enabled;
    }
    this.ReportRunEmailModel.create({
      reportRun: this.run.token,
      report: this.report.token,
      params: params,
    })
      .then(() => {
        this.toastService.success('Email sent');
      })
      .catch((e) => {
        this.errorReporter.notify({ error: e });
        this.toastService.error('Email failed to send');
      });
  }

  canEditSchedule(schedule: any) {
    return schedule._resource.hasForm('edit');
  }

  multiSelectParameterChanged(name: string, changes: [string | number, boolean][]) {
    const selectedAllWasToggled = changes.length === this.parameterOptionCount[name] && changes.length;

    if (selectedAllWasToggled) {
      // More efficient handling when selecting/deselecting all options
      const didSelectAll = changes[0][1];
      this.schedule.params[name] = didSelectAll ? [ALL_SELECTED_OPTION] : [];
      this.defaultOptions[name].forEach((o) => (o.isSelected = didSelectAll));
    } else {
      changes.forEach(([optionValue, selected]) => {
        if (selected) {
          this.schedule.params[name] = [...(this.schedule.params[name] ?? []), optionValue];
        } else {
          const allOptionsAreSelected =
            this.schedule.params[name].length === 1 && this.schedule.params[name][0] === ALL_SELECTED_OPTION;

          if (allOptionsAreSelected) {
            this.schedule.params[name] = this.defaultOptions[name].map((o) => o.value).filter((n) => n != optionValue);
          } else {
            this.schedule.params[name] = this.schedule.params[name].filter((n) => n != optionValue);
          }
        }
        if (this.schedule.params[name].length === this.parameterOptionCount[name]) {
          this.schedule.params[name] = [ALL_SELECTED_OPTION];
        }
        this.defaultOptions[name].filter((o) => o.value === optionValue).forEach((o) => (o.isSelected = selected));
      });
    }
    this.scheduleEditForm.$setDirty();
  }

  openConfirmDialog(event: Event) {
    // prevent bug where the modal navigates to the modal's user view
    event.preventDefault();

    this.confirmDialogService.open({
      dialogTitle: 'Delete schedule',
      dialogText: 'Are you sure you want to delete this schedule?',
      confirmText: 'Delete schedule',
      confirmAction: () => this.deleteSchedule(this.schedule),
    });
  }

  scheduleChanged(time: { hour: string; minute: string; timeZone: string }) {
    this.schedule.cron.hour = time.hour;
    this.schedule.cron.minute = time.minute;
    this.schedule.cron.time_zone = time.timeZone;
    this.scheduleModalFacade.scheduleChanged(this.schedule);
  }

  startTimeChanged(timeSelected: number) {
    this.schedule.cron.minute = timeSelected;
    this.scheduleModalFacade.startTimeChanged(timeSelected);
  }

  dayOfWeekChanged(daySelected: number) {
    const weekArray = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    this.schedule.cron.day_of_week = daySelected;
    this.schedule.day_of_week = weekArray[daySelected];
    this.dayOfTheWeek = this.schedule.day_of_week;
    this.scheduleModalFacade.scheduleChanged(this.schedule);
  }

  dayOfMonthChanged(dayOfMonthSelected: number) {
    this.schedule.cron.day_of_month = dayOfMonthSelected;
    this.scheduleModalFacade.scheduleChanged(this.schedule);
  }

  setHasStartTime() {
    const frequenciesWithStartTime = ['every_15_minutes', 'every_30_minutes', 'hourly'];
    this.hasStartTime = frequenciesWithStartTime.includes(this.scheduleModalFacade.frequencyType);
  }

  deliveryTypeChanged(type: string) {
    this.notify.type = type;
  }

  $onDestroy() {
    this.loadingScheduleFormSubscription?.unsubscribe();
    this.paramsSubscription?.unsubscribe();
  }

  updateParam(param) {
    this.schedule.params[param.name] = param.value;
    this.scheduleModalFacade.scheduleChanged(this.schedule);
  }

  updateMultiSelectAngular(param) {
    this.multiSelectParameterChanged(param.name, param.value);
  }

  backClicked() {
    this.scheduleModalFacade.backClicked();
    this.setView('index');
  }
}
